import sodium from "libsodium-wrappers-sumo";

let ready: Promise<void> | null = null;

/** Call once at app startup, before any other function in this file is used. */
export function initSodium(): Promise<void> {
  if (!ready) ready = sodium.ready;
  return ready;
}

export class DecryptionError extends Error {
  constructor(message = "Failed to decrypt") {
    super(message);
    this.name = "DecryptionError";
  }
}

export interface IdentityKeypair {
  publicKey: string; // base64
  privateKey: string; // base64
}

function toBase64(bytes: Uint8Array): string {
  return sodium.to_base64(bytes, sodium.base64_variants.ORIGINAL);
}

function fromBase64(b64: string): Uint8Array {
  return sodium.from_base64(b64, sodium.base64_variants.ORIGINAL);
}

// ---------- Identity keys (X25519, one per device) ----------
export function generateIdentityKeypair(): IdentityKeypair {
  const pair = sodium.crypto_box_keypair();
  return { publicKey: toBase64(pair.publicKey), privateKey: toBase64(pair.privateKey) };
}

/** X25519 public keys are always mathematically derivable from the private key -
 * used to verify a recovered private key actually corresponds to the account's
 * current public key before trusting it (see e2ee.ts's restoreIdentityFromRecovery). */
export function derivePublicKey(privateKeyB64: string): string {
  return toBase64(sodium.crypto_scalarmult_base(fromBase64(privateKeyB64)));
}

// ---------- Per-conversation symmetric key ----------
export function generateConversationKey(): Uint8Array {
  return sodium.crypto_secretbox_keygen();
}

export class InvalidPublicKeyError extends Error {
  constructor(message = "This public key isn't valid") {
    super(message);
    this.name = "InvalidPublicKeyError";
  }
}

// X25519 public keys are always exactly 32 bytes. A key that decodes to any other
// length can't belong to a real identity - most often this means the account was
// only ever created via a script/API call with a placeholder value and has never
// actually logged into a real browser session, since only logging in triggers
// generating and publishing a real key (see auth-context.tsx's ensureIdentity).
// Checking this explicitly, before ever handing the value to libsodium, turns what
// would otherwise be a raw, uncaught native crash ("invalid publicKey length") into
// a clear, catchable error every caller can handle the same way.
function assertValidPublicKey(publicKeyB64: string) {
  let decoded: Uint8Array;
  try {
    decoded = fromBase64(publicKeyB64);
  } catch {
    throw new InvalidPublicKeyError();
  }
  if (decoded.length !== sodium.crypto_box_PUBLICKEYBYTES) {
    throw new InvalidPublicKeyError();
  }
}

/** Wrap a conversation key for one recipient's public key. Server stores this opaquely. */
export function sealConversationKey(conversationKey: Uint8Array, recipientPublicKeyB64: string): string {
  assertValidPublicKey(recipientPublicKeyB64);
  const sealed = sodium.crypto_box_seal(conversationKey, fromBase64(recipientPublicKeyB64));
  return toBase64(sealed);
}

/** Unwrap a sealed conversation key using this device's own keypair. */
export function unsealConversationKey(
  sealedKeyB64: string,
  myPublicKeyB64: string,
  myPrivateKeyB64: string
): Uint8Array {
  try {
    // crypto_box_seal_open throws its own native error on failure (wrong keypair,
    // corrupted input) rather than returning null - normalize to DecryptionError.
    const opened = sodium.crypto_box_seal_open(
      fromBase64(sealedKeyB64),
      fromBase64(myPublicKeyB64),
      fromBase64(myPrivateKeyB64)
    );
    if (!opened) throw new DecryptionError("Could not unseal conversation key");
    return opened;
  } catch (err) {
    if (err instanceof DecryptionError) throw err;
    throw new DecryptionError("Could not unseal conversation key");
  }
}

// ---------- QR-code device login (see migration 0043 for the full design) ----------
// Same crypto_box_seal primitive as sealConversationKey above, just sealing a
// whole identity keypair (as JSON) instead of a single symmetric key. Called
// by the TRUSTED device, sealing against the ephemeral public key it scanned
// directly out of the QR code - never one fetched from the server, so a
// compromised server relaying a swapped-in key here can at most cause the new
// device's unseal to fail, not intercept the identity.
export function sealIdentityForNewDevice(identity: IdentityKeypair, ephemeralPublicKeyB64: string): string {
  assertValidPublicKey(ephemeralPublicKeyB64);
  const bytes = new TextEncoder().encode(JSON.stringify(identity));
  const sealed = sodium.crypto_box_seal(bytes, fromBase64(ephemeralPublicKeyB64));
  return toBase64(sealed);
}

/** Unwrap a sealed identity using the NEW device's own ephemeral keypair -
 * recovers the exact same identity the trusted device already has, which is
 * what makes every existing conversation immediately decryptable with zero
 * per-conversation re-linking. */
export function unsealIdentityFromTrustedDevice(
  sealedPayloadB64: string,
  ephemeralPublicKeyB64: string,
  ephemeralPrivateKeyB64: string
): IdentityKeypair {
  try {
    const opened = sodium.crypto_box_seal_open(
      fromBase64(sealedPayloadB64),
      fromBase64(ephemeralPublicKeyB64),
      fromBase64(ephemeralPrivateKeyB64)
    );
    if (!opened) throw new DecryptionError("Could not unseal identity");
    return JSON.parse(new TextDecoder().decode(opened)) as IdentityKeypair;
  } catch (err) {
    if (err instanceof DecryptionError) throw err;
    throw new DecryptionError("Could not unseal identity");
  }
}

// ---------- Message content (AEAD, bound to the conversation via associated data) ----------
function aad(conversationId: string): Uint8Array {
  return new TextEncoder().encode(conversationId);
}

export function encryptMessage(
  plaintext: string,
  conversationKey: Uint8Array,
  conversationId: string
): { content: string; nonce: string } {
  const nonce = sodium.randombytes_buf(sodium.crypto_aead_xchacha20poly1305_ietf_NPUBBYTES);
  const ciphertext = sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(
    new TextEncoder().encode(plaintext),
    aad(conversationId),
    null,
    nonce,
    conversationKey
  );
  return { content: toBase64(ciphertext), nonce: toBase64(nonce) };
}

export function decryptMessage(
  contentB64: string,
  nonceB64: string,
  conversationKey: Uint8Array,
  conversationId: string
): string {
  try {
    const plaintext = sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(
      null,
      fromBase64(contentB64),
      aad(conversationId),
      fromBase64(nonceB64),
      conversationKey
    );
    return new TextDecoder().decode(plaintext);
  } catch {
    throw new DecryptionError();
  }
}

// ---------- Chat media (images/files) - same AEAD scheme over raw bytes ----------
export function encryptBytes(bytes: Uint8Array, conversationKey: Uint8Array, conversationId: string): Uint8Array {
  const nonce = sodium.randombytes_buf(sodium.crypto_aead_xchacha20poly1305_ietf_NPUBBYTES);
  const ciphertext = sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(
    bytes,
    aad(conversationId),
    null,
    nonce,
    conversationKey
  );
  // nonce || ciphertext, so the file is self-contained - no separate nonce column for media
  const combined = new Uint8Array(nonce.length + ciphertext.length);
  combined.set(nonce, 0);
  combined.set(ciphertext, nonce.length);
  return combined;
}

export function decryptBytes(combined: Uint8Array, conversationKey: Uint8Array, conversationId: string): Uint8Array {
  const nonceLen = sodium.crypto_aead_xchacha20poly1305_ietf_NPUBBYTES;
  const nonce = combined.slice(0, nonceLen);
  const ciphertext = combined.slice(nonceLen);
  try {
    return sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(null, ciphertext, aad(conversationId), nonce, conversationKey);
  } catch {
    throw new DecryptionError();
  }
}

// ---------- Password-protected private-key backup ----------
// Lets someone recover their real identity (and therefore their old conversations)
// on a new device or after local storage is cleared, IF they remember a recovery
// passphrase they set up in advance - the same trade-off WhatsApp's encrypted
// backups and Signal's PIN-based recovery make. The passphrase itself never leaves
// the browser and the server never sees it: Argon2id (crypto_pwhash, deliberately
// slow and memory-hard to resist brute-forcing) derives a symmetric key from it,
// which encrypts the real private key before upload. The server only ever stores
// opaque ciphertext plus the random salt needed to re-derive the same key next
// time - it has zero ability to decrypt the backup itself.
export class WrongRecoveryPassphraseError extends Error {
  constructor(message = "That passphrase doesn't match") {
    super(message);
    this.name = "WrongRecoveryPassphraseError";
  }
}

export function generateRecoverySalt(): string {
  return toBase64(sodium.randombytes_buf(sodium.crypto_pwhash_SALTBYTES));
}

function deriveRecoveryKey(passphrase: string, saltB64: string): Uint8Array {
  return sodium.crypto_pwhash(
    sodium.crypto_secretbox_KEYBYTES,
    passphrase,
    fromBase64(saltB64),
    sodium.crypto_pwhash_OPSLIMIT_INTERACTIVE,
    sodium.crypto_pwhash_MEMLIMIT_INTERACTIVE,
    sodium.crypto_pwhash_ALG_ARGON2ID13
  );
}

/** Encrypts the raw private key with a passphrase-derived key, for server storage. */
export function encryptPrivateKeyForBackup(privateKeyB64: string, passphrase: string, saltB64: string): string {
  const key = deriveRecoveryKey(passphrase, saltB64);
  const nonce = sodium.randombytes_buf(sodium.crypto_secretbox_NONCEBYTES);
  const ciphertext = sodium.crypto_secretbox_easy(fromBase64(privateKeyB64), nonce, key);
  const combined = new Uint8Array(nonce.length + ciphertext.length);
  combined.set(nonce, 0);
  combined.set(ciphertext, nonce.length);
  return toBase64(combined);
}

/** Reverses encryptPrivateKeyForBackup. Throws WrongRecoveryPassphraseError on a wrong passphrase. */
export function decryptPrivateKeyBackup(encryptedBlobB64: string, passphrase: string, saltB64: string): string {
  const key = deriveRecoveryKey(passphrase, saltB64);
  const combined = fromBase64(encryptedBlobB64);
  const nonce = combined.slice(0, sodium.crypto_secretbox_NONCEBYTES);
  const ciphertext = combined.slice(sodium.crypto_secretbox_NONCEBYTES);
  try {
    const opened = sodium.crypto_secretbox_open_easy(ciphertext, nonce, key);
    return toBase64(opened);
  } catch {
    throw new WrongRecoveryPassphraseError();
  }
}
